<template>
  <modalWindow v-if="adding" @close="clearMessage();adding=false" width="460">
    <div class="titlebar">Profiel toevoegen</div>
    <div v-if="msg.msg" :class="['form-' + msg.type]">{{ msg.msg }}</div>
    <form @submit.prevent class="inset">
      <div class="edit-row">
        <div class="edit-input">
          <span>Profielnaam</span>
          <input type=text v-model="name"/>
        </div>
        <div class="edit-input">
          <span>Merk</span>
          <inputDropdown
            :array="brands"
            v-model="brand"
            style="margin-top:8px"
            backgroundColor="#ffffff"
          />
        </div>
      </div>
      <div class="edit-row">
        <div class="edit-input">
          <span>Gemiddeld jaarrendement (%)</span>
          <input type="number" min="0" v-model="averageAnnualReturnRate"/>
        </div>
        <div class="edit-input">
          <span>Min. horizon (dagen)</span>
          <input type="number" min="0" v-model="horizonMin"/>
        </div>
      </div>
      <div class="edit-row">
        <div class="edit-input">
          <span>Min. vreemde valuta (%)</span>
          <input type="number" min="0" v-model="foreignCurrencyMin"/>
        </div>
        <div class="edit-input">
          <span>Max. vreemde valuta (%)</span>
          <input type="number" min="0" v-model="foreignCurrencyMax"/>
        </div>
      </div>
      <div class="bandwidths">
        <h2>Bandbreedtes (%)</h2>
        <div class="bandwidths-col bandwidth-names" ref="modBandwidthNames">
          <div class="bandwidth-slot"></div>
          <div class="bandwidth-slot">
            <span>Aandelen</span>
          </div>
          <div class="bandwidth-slot">
            <span>Vast rentend</span>
          </div>
          <div class="bandwidth-slot">
            <span>Vastgoed</span>
          </div>
          <div class="bandwidth-slot">
            <span>Beschermd beleggen</span>
          </div>
          <div class="bandwidth-slot">
            <span>Alternatieven</span>
          </div>
          <div class="bandwidth-slot">
            <span>Liquiditeiten</span>
          </div>
        </div>
        <div class="bandwidths-col">
          <div class="bandwidth-slot top-slot">
            <span>Boven</span>
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="sharesMax"
              @change="updateBandwidth('shares', 'max')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="fixedInterestMax"
              @change="updateBandwidth('fixedInterest', 'max')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="propertyMax"
              @change="updateBandwidth('property', 'max')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="protectedInvestingMax"
              @change="updateBandwidth('protectedInvesting', 'max')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="alternativesMax"
              @change="updateBandwidth('alternatives', 'max')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="liquiditiesMax"
              @change="updateBandwidth('liquidities', 'max')"
              step="any"
            />
          </div>
        </div>
        <div class="bandwidths-col">
          <div class="bandwidth-slot top-slot">
            <span>Doel</span>
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="sharesTarget"
              @change="updateBandwidth('shares', 'target')"
              step="any"
              :class="{
                'calculated-bandwidth':
                  sharesTarget === ((parseInt(sharesMin, 10)
                  + parseInt(sharesMax, 10)) / 2)
              }"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="fixedInterestTarget"
              @change="updateBandwidth('fixedInterest', 'target')"
              step="any"
              :class="{
                'calculated-bandwidth':
                  fixedInterestTarget === ((parseInt(fixedInterestMin, 10)
                  + parseInt(fixedInterestMax, 10)) / 2)
              }"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="propertyTarget"
              @change="updateBandwidth('property', 'target')"
              step="any"
              :class="{
                'calculated-bandwidth':
                  propertyTarget === ((parseInt(propertyMin, 10)
                  + parseInt(propertyMax, 10)) / 2)
              }"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="protectedInvestingTarget"
              @change="updateBandwidth('protectedInvesting', 'target')"
              step="any"
              :class="{
                'calculated-bandwidth':
                  protectedInvestingTarget === ((parseInt(protectedInvestingMin, 10)
                  + parseInt(protectedInvestingMax, 10)) / 2)
              }"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="alternativesTarget"
              @change="updateBandwidth('alternatives', 'target')"
              step="any"
              :class="{
                'calculated-bandwidth':
                  alternativesTarget === ((parseInt(alternativesMin, 10)
                  + parseInt(alternativesMax, 10)) / 2)
              }"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="liquiditiesTarget"
              @change="updateBandwidth('liquidities', 'target')"
              step="any"
              :class="{
                'calculated-bandwidth':
                  liquiditiesTarget === ((parseInt(liquiditiesMin, 10)
                  + parseInt(liquiditiesMax, 10)) / 2)
              }"
            />
          </div>
        </div>
        <div class="bandwidths-col">
          <div class="bandwidth-slot top-slot">
            <span>Onder</span>
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="sharesMin"
              @change="updateBandwidth('shares', 'min')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="fixedInterestMin"
              @change="updateBandwidth('fixedInterest', 'min')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="propertyMin"
              @change="updateBandwidth('property', 'min')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="protectedInvestingMin"
              @change="updateBandwidth('protectedInvesting', 'min')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="alternativesMin"
              @change="updateBandwidth('alternatives', 'min')"
              step="any"
            />
          </div>
          <div class="bandwidth-slot">
            <input
              type="number"
              min="0"
              v-model="liquiditiesMin"
              @change="updateBandwidth('liquidities', 'min')"
              step="any"
            />
          </div>
        </div>
      </div>
      <input type=hidden ref="modId"/>
      <div class="edit-row submit-row">
        <span>Doelen check: {{ bandwidthCheck }} / 100%</span>
        <input type="submit" @click="createProfile" value="Profiel aanmaken"/>
      </div>
    </form>
  </modalWindow>
</template>

<script>
import { indexOf } from 'lodash-es';

export default {
  name: 'AddProfile',
  data() {
    return {
      adding: false,
      msg: { msg: null, type: 'notice' },
      brands: [
        {
          label: 'Doelbeleggen (DB)',
          value: 'Doelbeleggen',
        },
        {
          label: 'OXBY (OX)',
          value: 'OX',
        },
        {
          label: 'Beer & van Stapele (BVS)',
          value: 'BVS',
        },
        {
          label: 'Velthuyse · Mulder (VMVB)',
          value: 'VMVB',
        },
      ],
      id: null,
      name: null,
      brand: 'Doelbeleggen',
      averageAnnualReturnRate: null,
      horizonMin: null,
      foreignCurrencyMin: null,
      foreignCurrencyMax: null,
      sharesMin: 0,
      sharesTarget: 0,
      sharesMax: 0,
      fixedInterestMin: 0,
      fixedInterestTarget: 0,
      fixedInterestMax: 0,
      propertyMin: 0,
      propertyTarget: 0,
      propertyMax: 0,
      protectedInvestingMin: 0,
      protectedInvestingTarget: 0,
      protectedInvestingMax: 0,
      alternativesMin: 0,
      alternativesTarget: 0,
      alternativesMax: 0,
      liquiditiesMin: 0,
      liquiditiesTarget: 0,
      liquiditiesMax: 0,
      bandwidthCheck: 0,
    };
  },
  watch: {
    sharesTarget: 'updateAverage',
    fixedInterestTarget: 'updateAverage',
    propertyTarget: 'updateAverage',
    protectedInvestingTarget: 'updateAverage',
    alternativesTarget: 'updateAverage',
    liquiditiesTarget: 'updateAverage',
  },
  methods: {
    clearMessage() {
      this.msg = { msg: null, type: 'notice' };
    },
    updateBandwidth(name, targetInput) {
      let min = parseInt(this[`${name}Min`], 10);
      let max = parseInt(this[`${name}Max`], 10);
      let target = parseInt(this[`${name}Target`], 10);

      if (min > max && targetInput === 'min') {
        this[`${name}Min`] = max;
        min = parseInt(this[`${name}Min`], 10);
        target = parseInt(this[`${name}Target`], 10);
      } else if (max < min && targetInput === 'max') {
        this[`${name}Max`] = min;
        max = parseInt(this[`${name}Max`], 10);
        target = parseInt(this[`${name}Target`], 10);
      }

      if (targetInput === 'target') {
        if (target < min) {
          this[`${name}Target`] = min;
        } else if (target > max) {
          this[`${name}Target`] = max;
        }
      } else if (targetInput !== 'target') {
        this[`${name}Target`] = (min + max) / 2;
      } else if (target < min) {
        this[`${name}Target`] = min;
      } else if (target > max) {
        this[`${name}Target`] = max;
      }
    },
    updateAverage() {
      if (Number.isNaN(Number(this.sharesMin)) || this.sharesMin === '') {
        this.sharesMin = 0;
      }

      if (Number.isNaN(Number(this.sharesTarget)) || this.sharesTarget === '') {
        this.sharesTarget = 0;
      }

      if (Number.isNaN(Number(this.sharesMax)) || this.sharesMax === '') {
        this.sharesMax = 0;
      }

      if (Number.isNaN(Number(this.fixedInterestMin)) || this.fixedInterestMin === '') {
        this.fixedInterestMin = 0;
      }

      if (Number.isNaN(Number(this.fixedInterestTarget)) || this.fixedInterestTarget === '') {
        this.fixedInterestTarget = 0;
      }

      if (Number.isNaN(Number(this.fixedInterestMax)) || this.fixedInterestMax === '') {
        this.fixedInterestMax = 0;
      }

      if (Number.isNaN(Number(this.propertyMin)) || this.propertyMin === '') {
        this.propertyMin = 0;
      }

      if (Number.isNaN(Number(this.propertyTarget)) || this.propertyTarget === '') {
        this.propertyTarget = 0;
      }

      if (Number.isNaN(Number(this.propertyMax)) || this.propertyMax === '') {
        this.propertyMax = 0;
      }

      if (Number.isNaN(Number(this.protectedInvestingMin)) || this.protectedInvestingMin === '') {
        this.protectedInvestingMin = 0;
      }

      if (Number.isNaN(Number(this.protectedInvestingTarget)) || this.protectedInvestingTarget === '') {
        this.protectedInvestingTarget = 0;
      }

      if (Number.isNaN(Number(this.protectedInvestingMax)) || this.protectedInvestingMax === '') {
        this.protectedInvestingMax = 0;
      }

      if (Number.isNaN(Number(this.alternativesMin)) || this.alternativesMin === '') {
        this.alternativesMin = 0;
      }

      if (Number.isNaN(Number(this.alternativesTarget)) || this.alternativesTarget === '') {
        this.alternativesTarget = 0;
      }

      if (Number.isNaN(Number(this.alternativesMax)) || this.alternativesMax === '') {
        this.alternativesMax = 0;
      }

      if (Number.isNaN(Number(this.liquiditiesMin)) || this.liquiditiesMin === '') {
        this.liquiditiesMin = 0;
      }

      if (Number.isNaN(Number(this.liquiditiesTarget)) || this.liquiditiesTarget === '') {
        this.liquiditiesTarget = 0;
      }

      if (Number.isNaN(Number(this.liquiditiesMax)) || this.liquiditiesMax === '') {
        this.liquiditiesMax = 0;
      }

      this.bandwidthCheck = Number(this.sharesTarget)
        + Number(this.fixedInterestTarget)
        + Number(this.propertyTarget)
        + Number(this.protectedInvestingTarget)
        + Number(this.alternativesTarget)
        + Number(this.liquiditiesTarget);
    },
    profileCreate() {
      this.adding = true;
      this.$nextTick(() => {
        document.querySelectorAll('.bandwidths input[type="number"]').forEach((el) => {
          el.addEventListener('focus', (e) => {
            this.bandwidthInputFocus(e, true);
          });
          el.addEventListener('focusout', (e) => {
            this.bandwidthInputFocus(e);
          });
        });
      });
    },
    createProfile() {
      if (
        this.name === ''
        || this.brand === ''
        || this.averageAnnualReturnRate === ''
        || this.horizonMin === ''
        || this.foreignCurrencyMin === ''
        || this.foreignCurrencyMax === ''
      ) {
        this.msg = { msg: 'Niet alle velden zijn ingevuld', type: 'error' };
        return;
      }

      if (this.horizonMin < 365) {
        this.msg = { msg: 'Minimale horizon moet minstens 365 dagen zijn', type: 'error' };
        return;
      }

      if (
        this.foreignCurrencyMin < 0
        || this.foreignCurrencyMax < 0
        || this.sharesMin < 0
        || this.sharesMax < 0
        || this.fixedInterestMin < 0
        || this.fixedInterestMax < 0
        || this.propertyMin < 0
        || this.propertyMax < 0
        || this.protectedInvestingMin < 0
        || this.protectedInvestingMax < 0
        || this.alternativesMin < 0
        || this.alternativesMax < 0
        || this.liquiditiesMin < 0
        || this.liquiditiesMax < 0
      ) {
        this.msg = { msg: 'Een bandbreedte mag niet minder dan 0% zijn', type: 'error' };
        return;
      }

      if (
        this.foreignCurrencyMin < 0
        || this.foreignCurrencyMax < 0
        || this.sharesMin < 0
        || this.sharesMax < 0
        || this.fixedInterestMin < 0
        || this.fixedInterestMax < 0
        || this.propertyMin < 0
        || this.propertyMax < 0
        || this.protectedInvestingMin < 0
        || this.protectedInvestingMax < 0
        || this.alternativesMin < 0
        || this.alternativesMax < 0
        || this.liquiditiesMin < 0
        || this.liquiditiesMax < 0
      ) {
        this.msg = { msg: 'Een bandbreedte mag niet meer dan 100% zijn', type: 'error' };
        return;
      }

      if (this.bandwidthCheck !== 100) {
        this.msg = { msg: 'De totaalwaarde van alle doelen opgeteld moet 100 zijn', type: 'error' };
        return;
      }

      this.$nextTick(() => {
        this.sdk.createProfile(
          {
            id: this.id,
            name: this.name,
            brand: this.brand,
            average_annual_return_rate: this.averageAnnualReturnRate,
            horizon_min: this.horizonMin,
            foreign_currency_min: this.foreignCurrencyMin,
            foreign_currency_max: this.foreignCurrencyMax,
            shares_min: this.sharesMin,
            shares_target: this.sharesTarget,
            shares_max: this.sharesMax,
            fixed_interest_min: this.fixedInterestMin,
            fixed_interest_target: this.fixedInterestTarget,
            fixed_interest_max: this.fixed_interestMax,
            property_min: this.propertyMin,
            property_target: this.propertyTarget,
            property_max: this.propertyMax,
            protected_investing_min: this.protectedInvestingMin,
            protected_investing_target: this.protectedInvestingTarget,
            protected_investing_max: this.protectedInvestingMax,
            alternatives_min: this.alternativesMin,
            alternatives_target: this.alternativesTarget,
            alternatives_max: this.alternativesMax,
            liquidities_min: this.liquiditiesMin,
            liquidities_target: this.liquiditiesTarget,
            liquidities_max: this.liquiditiesMax,
          },
          (response) => {
            if (response.status === 200) {
              this.$parent.getProfiles();
              this.adding = false;
            } else {
              this.msg = { msg: 'Toevoeging niet verwerkt', type: 'error' };
            }
          },
        );
      });
    },
    bandwidthInputFocus(e, dir) {
      if (dir === true) {
        e.currentTarget.parentElement.parentElement.querySelector('span').classList.add('selected');
        const index = indexOf(
          e.currentTarget.parentElement.parentElement.children,
          e.currentTarget.parentElement,
        );
        this.$refs.modBandwidthNames.querySelectorAll('.bandwidth-slot')[index].firstElementChild.classList.add('selected');
      } else {
        e.currentTarget.parentElement.parentElement.querySelector('span').classList.remove('selected');
        this.$refs.modBandwidthNames.querySelectorAll('.bandwidth-slot').forEach((el) => {
          if (el.querySelector('span')) {
            el.querySelector('span').classList.remove('selected');
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  form {
    padding: 15px 20px 20px 20px;
  }

  .calculated-bandwidth {
    color: $color-highlight;
  }

  .edit-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 10px;

    .edit-input {
      width: 200px;
      height: auto;
      display: flex;
      flex-direction: column;
    }

    input[type="text"],
    input[type="number"] {
      width: 200px;
      border-radius: 0;
      margin-top: 8px !important;
    }

    input[type="submit"] {
      width: 200px;
    }

    select {
      width: 200px;
      vertical-align: top;
      margin: 0;
      cursor: pointer;
      border-radius: 0;
      margin-top: 8px !important;
    }
  }

  .submit-row {
    justify-content: space-between;
    margin-bottom: 0;
    margin-top: 10px;

    input {
      margin: 0 !important;
    }
  }

  .bandwidths {
    width: 100%;
    height: auto;
    position: relative;
    padding-top: 40px;

    span {
      border: 2px solid transparent;
      padding: 2px;
      transition: border-color 0.15s;
      border-radius: 2px;

      &.selected {
        border: 2px solid $color-highlight;
      }
    }

    h2 {
      position: absolute;
      top: 0;
      left: 0;
    }

    .bandwidths-col {
      width: 80px;
      height: auto;
      display: inline-block;
      margin-right: 10px;

      &:last-child {
        margin-right: 0;
      }

      &.bandwidth-names {
        width: 140px;
        vertical-align: top;
        margin-right: 20px;
      }

      .bandwidth-slot {
        width: 100%;
        height: 36px;
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        justify-content: flex-end;

        input {
          border-radius: 0;
        }

        &:first-child {
          height: 20px;
        }

        &.top-slot {
          justify-content: center;
        }
      }
    }
  }
</style>
